/**
 * Purchases report — spend on approved e-invoices, broken down by
 * supplier category over time, plus a per-supplier ranking.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FileText, Truck, Wallet } from 'lucide-react';
import { supabase } from '../supabase.js';
import { fetchPaged } from '../fetchPaged.js';
import { formatAmd, resolveAppLocale } from '../locale.js';
import {
  Callout,
  Card,
  CardHeader,
  chartColor,
  CHART_TICK,
  CHART_TOOLTIP,
  DateRange,
  EmptyState,
  PageHeader,
  resolveDateRange,
  Stat,
  Stats,
  StatusPill,
  type DateRangeValue,
} from '../ui/index.js';

const UNCATEGORIZED = 'uncategorized';

interface InvRow {
  id: string;
  supplier_tin: string;
  supplier_name: string;
  total: number;
  issued_at: string | null;
}

interface SupplierInfo {
  id: string;
  tin: string;
  display_name: string;
  category: string;
}

interface SupplierAgg {
  tin: string;
  name: string;
  category: string;
  registered: boolean;
  invoices: number;
  spend: number;
}

function monthLabel(key: string, locale: string): string {
  const [y, m] = key.split('-');
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString(locale, { month: 'short', year: '2-digit' });
}

export function PurchasesView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const fmtAMD = (n: number) => formatAmd(n, i18n.language);
  const [range, setRange] = useState<DateRangeValue>({ preset: 'this_year' });
  const { from: fromDate, to: toDate } = resolveDateRange(range);
  const rangeReady = range.preset !== 'custom' || (!!range.from && !!range.to);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<InvRow[]>([]);
  const [suppliersByTin, setSuppliersByTin] = useState<Map<string, SupplierInfo>>(new Map());

  const catLabel = useCallback(
    (c: string) => (c === UNCATEGORIZED ? t('purchases.uncategorized') : t(`suppliers.cat_${c}`, c)),
    [t],
  );

  const load = useCallback(async () => {
    if (!supabase || !clientId || !rangeReady) return;
    const db = supabase;
    setLoading(true);
    setError(null);
    try {
      const [invRows, supRows] = await Promise.all([
        fetchPaged((from, to) => {
          let q = db
            .from('invoices')
            .select('id, supplier_tin, supplier_name, total, issued_at')
            .eq('client_id', clientId)
            .eq('internal_status', 'approved');
          if (fromDate) q = q.gte('issued_at', fromDate);
          if (toDate) q = q.lte('issued_at', `${toDate}T23:59:59`);
          return q.order('issued_at', { ascending: true }).range(from, to);
        }),
        db.from('suppliers').select('id, tin, display_name, category').eq('client_id', clientId),
      ]);
      if (supRows.error) throw new Error(supRows.error.message);
      setInvoices(invRows as InvRow[]);
      setSuppliersByTin(
        new Map((supRows.data ?? []).map((s) => [s.tin, s as SupplierInfo])),
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('purchases.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [clientId, fromDate, toDate, rangeReady, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const categoryOf = useCallback(
    (tin: string) => suppliersByTin.get(tin)?.category || UNCATEGORIZED,
    [suppliersByTin],
  );

  // ── Monthly spend by category (stacked bars) ──
  const { chartData, categories } = useMemo(() => {
    const byMonth = new Map<string, Map<string, number>>();
    const catTotals = new Map<string, number>();
    for (const inv of invoices) {
      if (!inv.issued_at) continue;
      const month = inv.issued_at.slice(0, 7);
      const cat = categoryOf(inv.supplier_tin);
      let cats = byMonth.get(month);
      if (!cats) {
        cats = new Map();
        byMonth.set(month, cats);
      }
      cats.set(cat, (cats.get(cat) ?? 0) + Number(inv.total));
      catTotals.set(cat, (catTotals.get(cat) ?? 0) + Number(inv.total));
    }
    // Biggest categories first so stack + legend order is meaningful.
    const cats = [...catTotals.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
    const data = [...byMonth.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, values]) => {
        const row: Record<string, number | string> = { month: monthLabel(month, locale) };
        for (const c of cats) row[c] = values.get(c) ?? 0;
        return row;
      });
    return { chartData: data, categories: cats };
  }, [invoices, categoryOf, locale]);

  // ── Per-supplier ranking ──
  const supplierRows = useMemo(() => {
    const byTin = new Map<string, SupplierAgg>();
    for (const inv of invoices) {
      const info = suppliersByTin.get(inv.supplier_tin);
      let agg = byTin.get(inv.supplier_tin);
      if (!agg) {
        agg = {
          tin: inv.supplier_tin,
          name: info?.display_name || inv.supplier_name || inv.supplier_tin,
          category: categoryOf(inv.supplier_tin),
          registered: !!info,
          invoices: 0,
          spend: 0,
        };
        byTin.set(inv.supplier_tin, agg);
      }
      agg.invoices += 1;
      agg.spend += Number(inv.total);
    }
    return [...byTin.values()].sort((a, b) => b.spend - a.spend);
  }, [invoices, suppliersByTin, categoryOf]);

  const totalSpend = useMemo(
    () => invoices.reduce((s, i) => s + Number(i.total), 0),
    [invoices],
  );

  const emptyText = loading ? t('common.loading') : t('purchases.emptyPeriod');

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('purchases.title')}
        subtitle={t('purchases.subtitle')}
        actions={<DateRange value={range} onChange={setRange} />}
      />

      {error && (
        <Callout tone="danger" title={t('purchases.couldntLoad')}>
          {error}
        </Callout>
      )}

      <Stats>
        <Stat icon={<Wallet />} label={t('purchases.totalSpend')} value={fmtAMD(totalSpend)} />
        <Stat icon={<FileText />} label={t('purchases.approvedInvoices')} value={invoices.length} />
        <Stat icon={<Truck />} label={t('purchases.suppliers')} value={supplierRows.length} />
      </Stats>

      <Card>
        <CardHeader title={t('purchases.chartTitle')} />
        {chartData.length === 0 ? (
          <EmptyState compact title={t('purchases.nothingToChart')} text={emptyText} />
        ) : (
          <div className="chart-300">
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                <XAxis dataKey="month" tick={CHART_TICK} tickLine={false} axisLine={false} />
                <YAxis
                  tick={CHART_TICK}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) =>
                    v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : `${Math.round(v / 1000)}k`
                  }
                />
                <Tooltip
                  contentStyle={CHART_TOOLTIP}
                  formatter={(value, key) => [fmtAMD(Number(value ?? 0)), catLabel(String(key))]}
                />
                <Legend formatter={(key: string) => catLabel(key)} iconType="circle" />
                {categories.map((c, i) => (
                  <Bar key={c} dataKey={c} stackId="spend" fill={chartColor(i)} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card layout="flush">
        <CardHeader title={t('purchases.bySupplier')} />
        {supplierRows.length === 0 ? (
          <EmptyState compact title={t('purchases.noSuppliers')} text={emptyText} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('suppliers.colName')}</th>
                <th>{t('suppliers.colCategory')}</th>
                <th className="num">{t('purchases.colInvoices')}</th>
                <th className="num">{t('purchases.colSpend')}</th>
                <th className="num">{t('purchases.colShare')}</th>
              </tr>
            </thead>
            <tbody>
              {supplierRows.map((s) => (
                <tr key={s.tin}>
                  <td>
                    {s.registered ? (
                      <button
                        className="link"
                        onClick={() => {
                          window.location.hash = `/suppliers?tin=${encodeURIComponent(s.tin)}`;
                        }}
                        title={t('purchases.openSummary')}
                      >
                        {s.name}
                      </button>
                    ) : (
                      s.name
                    )}
                  </td>
                  <td>
                    <StatusPill status={s.category} tone="neutral" dot={false}>
                      {catLabel(s.category)}
                    </StatusPill>
                  </td>
                  <td className="num muted">{s.invoices}</td>
                  <td className="num">{fmtAMD(s.spend)}</td>
                  <td className="num muted">
                    {totalSpend > 0 ? `${((s.spend / totalSpend) * 100).toFixed(1)}%` : t('common.emptyDash')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </section>
  );
}
